import UserApi from '../../rest-api/user-api';

/**
 * Removes patient whiteboards from userData.whiteboards list
 * Patient whiteboards should only appear in "My Clinic", not "My Library"
 * This fixes data corruption from earlier bugs
 */
export const cleanupPatientWhiteboardsFromLibrary = async (userDataStore, whiteboardStore, authStore) => {
    try {
        const allWhiteboards = Object.values(whiteboardStore.whiteboardData);
        const userWhiteboardIds = userDataStore.userData.whiteboards || [];

        // Find whiteboards that have patientId but are in userData.whiteboards
        const patientWhiteboardIds = allWhiteboards
            .filter(wb => wb.patientId && userWhiteboardIds.includes(wb.whiteboardId))
            .map(wb => wb.whiteboardId);

        if (patientWhiteboardIds.length === 0) {
            console.log('No patient whiteboards found in My Library list - data is clean');
            return { cleaned: false, count: 0 };
        }

        console.warn(`Found ${patientWhiteboardIds.length} patient whiteboards incorrectly in My Library:`, patientWhiteboardIds);

        // Remove patient whiteboard IDs from userData.whiteboards
        const cleanedWhiteboards = userWhiteboardIds.filter(id => !patientWhiteboardIds.includes(id));
        const cleanedFavourites = (userDataStore.userData.whiteboardsFavourites || []).filter(id => !patientWhiteboardIds.includes(id));

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            whiteboards: cleanedWhiteboards,
            whiteboardsFavourites: cleanedFavourites,
        });

        // Sync to backend
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const updatePayload = {
            whiteboards: cleanedWhiteboards,
            whiteboardsFavourites: cleanedFavourites,
        };

        if (isMultiUser) {
            updatePayload.action = 'updateMultiuser';
            updatePayload.emailAddress = userDataStore.userData.emailAddress;
            updatePayload.inviteId = userDataStore.userData.inviteId;
            await UserApi.updateClinicianDataMultiuser(authStore.idToken, updatePayload);
        } else {
            await UserApi.updateClinicianData(authStore.idToken, updatePayload);
        }

        console.warn(`Cleaned up ${patientWhiteboardIds.length} patient whiteboards from My Library`);
        return { cleaned: true, count: patientWhiteboardIds.length };
    } catch (err) {
        console.error('Failed to cleanup patient whiteboards:', err);
        throw err;
    }
};

export const archiveWhiteboard = async (
    wb,
    userDataStore,
    whiteboardStore,
    authStore,
    setWhiteboards,
    setFilteredWhiteboards,
    showSnackbar,
) => {
    try {
        const payload = {
            whiteboardId: wb.whiteboardId,
            fileName: wb.fileName,
            updatedById: userDataStore.userId,
            whiteboardStatus: 'archived',
        };
        const resp = await UserApi.createOrUpdateWhiteboard(authStore.idToken, payload);
        console.log('Archive response:', resp);

        // Exclude non-serializable fields before storing in IndexedDB
        const { lastUsedColours, ...rest } = wb;
        const archivedWB = { ...rest, whiteboardStatus: 'archived' };
        await whiteboardStore.setWhiteboardData([archivedWB]);

        setWhiteboards((prev) => prev.filter((old) => old.whiteboardId !== wb.whiteboardId));
        setFilteredWhiteboards((prev) => prev.filter((old) => old.whiteboardId !== wb.whiteboardId));

        // Only update userData.whiteboards if this whiteboard was in "My Library" (no patientId)
        // Patient whiteboards only appear in "My Clinic", not "My Library"
        if (!wb.patientId) {
            const updatedWhiteboards = userDataStore.userData.whiteboards.filter((id) => id !== wb.whiteboardId);
            const updatedFavourites = userDataStore.userData.whiteboardsFavourites.filter((id) => id !== wb.whiteboardId);

            userDataStore.setUserData({
                ...userDataStore.userData,
                whiteboards: updatedWhiteboards,
                whiteboardsFavourites: updatedFavourites,
            });

            const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
            const updatePayload = {
                whiteboards: updatedWhiteboards,
                whiteboardsFavourites: updatedFavourites,
            };

            try {
                if (isMultiUser) {
                    updatePayload.action = 'updateMultiuser';
                    updatePayload.emailAddress = userDataStore.userData.emailAddress;
                    updatePayload.inviteId = userDataStore.userData.inviteId;
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, updatePayload);
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                }
            } catch (err) {
                console.error('Failed to sync archived whiteboard update to backend', err);
            }
        }

        showSnackbar && showSnackbar('Whiteboard archived successfully', 'success');
    } catch (err) {
        console.error('Failed to archive whiteboard', err);
        showSnackbar && showSnackbar('Failed to archive whiteboard', 'error');
    }
};

export const recoverWhiteboard = async (
    wb,
    userDataStore,
    whiteboardStore,
    authStore,
    setArchivedWhiteboards,
    setFiltered,
    showSnackbar,
) => {
    try {
        const payload = {
            whiteboardId: wb.whiteboardId,
            fileName: wb.fileName,
            updatedById: userDataStore.userId,
            whiteboardStatus: 'active',
        };

        const resp = await UserApi.createOrUpdateWhiteboard(authStore.idToken, payload);
        console.log('Recover response:', resp);

        // Exclude non-serializable fields before storing in IndexedDB
        const { lastUsedColours, ...rest } = wb;
        const recoveredWB = { ...rest, whiteboardStatus: 'active' };
        await whiteboardStore.setWhiteboardData([recoveredWB]);

        // Remove from archived lists
        setArchivedWhiteboards((prev) => prev.filter((w) => w.whiteboardId !== wb.whiteboardId));
        setFiltered((prev) => prev.filter((w) => w.whiteboardId !== wb.whiteboardId));

        // Only add back to user's active list if it's NOT assigned to a patient
        // Whiteboards with patientId should only appear in "My Clinic", not "My Library"
        if (!wb.patientId) {
            const updatedWhiteboards = [...userDataStore.userData.whiteboards, wb.whiteboardId];

            userDataStore.setUserData({
                ...userDataStore.userData,
                whiteboards: updatedWhiteboards,
            });

            // Sync to backend
            const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
            const updatePayload = {
                whiteboards: updatedWhiteboards,
            };

            try {
                if (isMultiUser) {
                    updatePayload.action = 'updateMultiuser';
                    updatePayload.emailAddress = userDataStore.userData.emailAddress;
                    updatePayload.inviteId = userDataStore.userData.inviteId;
                    await UserApi.updateClinicianDataMultiuser(authStore.idToken, updatePayload);
                } else {
                    await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                }
            } catch (err) {
                console.error('Failed to sync recovery to backend', err);
            }
        }

        showSnackbar && showSnackbar('Whiteboard recovered!', 'success');
    } catch (err) {
        console.error('Failed to recover whiteboard', err);
        showSnackbar && showSnackbar('Failed to recover whiteboard', 'error');
    }
};
